import React from 'react';
import {
	Box,
	Card,
	Checkbox,
	FormControlLabel,
	Typography,
} from '@mui/material';
import { useFormContext, Controller, useWatch } from 'react-hook-form';
import { ParkingLocation } from 'containers/pages/CANpark/types/parkingLocation';
import FlowPreview from 'containers/pages/CANpark/components/flowStepper/FlowPreview';

export const WorkflowOptionsSection: React.FC<{ loading: boolean }> = ({
	loading,
}) => {
	const { control } = useFormContext<ParkingLocation>();
	const watched = useWatch({ control });

	return (
		<Card sx={{ padding: 1 }}>
			<Typography variant="body2" fontWeight={300}>
				Workflow Options
			</Typography>
			<Typography
				variant="caption"
				color="text.secondary"
				component="div"
				sx={{ pb: 1 }}
			>
				Toggle each stage on or off. The preview below updates as you
				change settings.
			</Typography>

			<Controller
				control={control}
				name="showScanPreview"
				render={({ field }) => (
					<Box>
						<FormControlLabel
							control={
								<Checkbox
									checked={!!field.value}
									onChange={(e) =>
										field.onChange(e.target.checked)
									}
									disabled={loading}
								/>
							}
							label={
								<Typography variant="caption">
									Show scan preview before opening the record
									flow
								</Typography>
							}
						/>
						<Typography
							variant="caption"
							color="text.secondary"
							component="div"
							sx={{ pl: 4, pr: 1, pb: 0.5 }}
						>
							When off, a clean license-plate scan opens the
							drawer immediately without a confirmation preview.
						</Typography>
					</Box>
				)}
			/>

			<Controller
				control={control}
				name="showActionStage"
				render={({ field }) => (
					<Box>
						<FormControlLabel
							control={
								<Checkbox
									checked={!!field.value}
									onChange={(e) =>
										field.onChange(e.target.checked)
									}
									disabled={loading}
								/>
							}
							label={
								<Typography variant="caption">
									Show action stage (vehicle history + Record
									/ Warning / Tow buttons)
								</Typography>
							}
						/>
						<Typography
							variant="caption"
							color="warning.main"
							component="div"
							sx={{ pl: 4, pr: 1, pb: 0.5 }}
						>
							When off, the recommended action is auto-accepted
							and the operator skips straight to the next stage
							without seeing the vehicle's history or violation
							reason. Operators can still change the action on
							the record form. Disable only on lots where you
							trust the parking rules to make the right call.
						</Typography>
					</Box>
				)}
			/>

			<Controller
				control={control}
				name="showCarPhotoStage"
				render={({ field }) => (
					<Box>
						<FormControlLabel
							control={
								<Checkbox
									checked={!!field.value}
									onChange={(e) =>
										field.onChange(e.target.checked)
									}
									disabled={loading}
								/>
							}
							label={
								<Typography variant="caption">
									Show vehicle photo stage
								</Typography>
							}
						/>
						<Typography
							variant="caption"
							color="text.secondary"
							component="div"
							sx={{ pl: 4, pr: 1, pb: 0.5 }}
						>
							Insert a dedicated photo capture step after the
							operator picks an action. Operators can still skip
							if needed.
						</Typography>
					</Box>
				)}
			/>

			<Controller
				control={control}
				name="showConfirmStage"
				render={({ field }) => (
					<Box>
						<FormControlLabel
							control={
								<Checkbox
									checked={!!field.value}
									onChange={(e) =>
										field.onChange(e.target.checked)
									}
									disabled={loading}
								/>
							}
							label={
								<Typography variant="caption">
									Show confirmation stage before submitting a
									record
								</Typography>
							}
						/>
						<Typography
							variant="caption"
							color="text.secondary"
							component="div"
							sx={{ pl: 4, pr: 1, pb: 0.5 }}
						>
							When off, records are submitted directly from the
							record form without a separate review step.
						</Typography>
					</Box>
				)}
			/>

			<Box
				data-tour="operator-flow-preview"
				sx={{
					mt: 2,
					p: 2,
					borderRadius: 2,
					border: 1,
					borderColor: 'divider',
					bgcolor: 'background.default',
				}}
			>
				<Typography
					variant="caption"
					color="text.secondary"
					component="div"
					sx={{ mb: 1 }}
				>
					Operator flow preview
				</Typography>
				<FlowPreview lot={watched as Partial<ParkingLocation>} />
			</Box>
		</Card>
	);
};
